import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  ClickAwayListener,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tooltip
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import {
  DataTable,
  FaIcon,
  FullPageLoader,
  If,
  MDBox,
  MDButton,
  MDInput,
  MDTypography
} from '@components/index';
import {
  useCombineDataFilesMutation,
  useGetProjectFieldListQuery,
  useUpdateProjectFieldDetailMutation
} from '@api/projects';
import { ColumnTypes } from '@constants/common';

import {
  setCombineProjectFields,
  setCombineProjectFieldImportStatus,
  setCombineProjectFieldName,
  setCombineProjectFieldType
} from '@pages/ProjectBoard/slice';
import { RootState } from '@store/store';
import { convertToExcelColumnNumber } from '@utils/common';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { setUserNotifications } from '@components/Notification/slice';
import { NotificationData, MSG_EVENT, MSG_TYPE } from '@DStypes/notifications';
import { sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';
import {
  CLICKED_IMPORT_NEW_FILE,
  CLICKED_KNOW_MORE,
  FILE_REVIEW,
  INFO_SENSITIVE
} from '@constants/eventNames';
import { getColumnFacts } from '@pages/ProjectReview';
import { CombineDataPayload } from './CombineFilesContent';

interface SensitiveCell {
  id: string;
  fieldname: string;
  treat_as_type: string;
  to_import: boolean;
  contains_sensitive: boolean;
  field_fav: boolean;
  data_facts: string;
  clean_percentage: string;
  empty_rows: number;
  unique_values: number;
  file_field_type_counts: string;
}
export const formatColumnName = (column: string) => {
  if (column.length <= 50) return column;

  return `${column.slice(0, 30)}...${column.slice(-17)}`;
};

const CombineReviewTable: FC<{
  fileId: string;
  projectName: string;
  combineDataPayload?: CombineDataPayload;
  setInitiatePolling: React.Dispatch<React.SetStateAction<boolean>>;
  handleCancelCombine: () => void;
}> = ({
  fileId,
  projectName,
  combineDataPayload,
  setInitiatePolling,
  handleCancelCombine
}) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const excludedTypes = ['DATE', 'TIME'];

  const projectBoard = useSelector((state: RootState) => state?.projectBoard);

  const combineProjectFieldList = projectBoard?.combineProjectFieldList;

  const { data, isLoading } = useGetProjectFieldListQuery({
    projectId,
    fileId
  });
  const [updateColumnDetails] = useUpdateProjectFieldDetailMutation();

  const [combineDataFiles, { isSuccess: isCombineSuccess }] =
    useCombineDataFilesMutation();

  const [openAlertSnackBar, setOpenAlertSnackBar] = useState(false);
  const handleCloseSnackbar = () => setOpenAlertSnackBar(false);

  const [selectedSensitiveColumns, setSelectedSensitiveColumns] = useState<
    SensitiveCell[]
  >([]);

  const defaultDataTypes = data?.fields?.map(
    (field: any) => field.treat_as_type
  );

  // hook to trigger key field as true, only if to_import is false initially
  useEffect(() => {
    const keyfield = combineProjectFieldList?.fields?.find(
      (field: any) => field.id === combineDataPayload?.selected_source_keyfield
    );

    if (
      combineDataPayload?.selected_source_keyfield &&
      combineProjectFieldList?.fields?.length &&
      !keyfield?.to_import
    ) {
      dispatch(
        setCombineProjectFieldImportStatus({
          id: combineDataPayload?.selected_source_keyfield,
          importStatus: true
        })
      );
      updateColumnDetails({
        id: combineDataPayload?.selected_source_keyfield,
        to_import: true
      });
    }
  }, [
    combineDataPayload?.selected_source_keyfield,
    combineProjectFieldList?.fields?.length
  ]);

  const handleImportSwitchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string,
    type: string,
    cell: any
  ) => {
    if (id) {
      dispatch(
        setCombineProjectFieldImportStatus({
          id: id,
          importStatus: e.target.checked
        })
      );
      updateColumnDetails({
        id: id,
        to_import: e.target.checked
      });

      if (cell.row.original.contains_sensitive === true && e.target.checked)
        setOpenAlertSnackBar(true);
    }
  };

  const handleColumnTypeChange = (e: any, id: string, to_import: boolean) => {
    if (id) {
      dispatch(setCombineProjectFieldType({ id: id, type: e.target.value }));
      updateColumnDetails({
        treat_as_type: e.target.value,
        id: id,
        to_import: to_import
      });
    }
  };

  const EditableCell = ({ value: initialValue, row }: any) => {
    const [value, setValue] = useState(initialValue);
    const [showText, setShowText] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleUpdateColumnName = ({ value, row }: any) => {
      const columnNames = combineProjectFieldList?.fields?.map(
        (field: any) => field.fieldname
      );

      const duplicateColumnNames = columnNames.filter(
        (name: string) => name === value.trim()
      );

      if (duplicateColumnNames.length && value.trim() !== initialValue) {
        setError(true);
        setErrorMessage('This column name already exists.');
      } else {
        dispatch(
          setCombineProjectFieldName({ id: row.original.id, value: value })
        );
        setShowText(true);
        if (value.trim() !== initialValue && row.original.id)
          updateColumnDetails({
            id: row.original.id,
            fieldname: value
          });
      }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value.length > 50) {
        setError(true);
        setErrorMessage('Character limit 50 exceeded');
      } else {
        setError(false);
        setErrorMessage('');
      }
      setValue(e.target.value);
    };

    const handleEnterAndAwayClick = () => {
      if (value.trim().length && !error) handleUpdateColumnName({ value, row });
      else setError(true);
    };

    const typographyRef = useRef<HTMLDivElement>();

    const showTooltip =
      typographyRef?.current &&
      typographyRef?.current?.getBoundingClientRect().height > 19
        ? true
        : false;

    return showText ? (
      <>
        {row.original.contains_sensitive === true ? (
          <Grid container spacing={1} display='flex' alignItems='center'>
            <Grid item onClick={() => sendAmplitudeData(INFO_SENSITIVE)}>
              <Tooltip
                title='This column may contain sensitive data (Ex: Name, e-mail ID, Payment details).'
                placement='right'
              >
                <a
                  href='https://www.notion.so/What-is-Sensitive-Data-Data-Anonymisation-17f688499d2045d99ec677c037f0c290'
                  target='_blank'
                  rel='noreferrer'
                >
                  <IconButton sx={{ mb: '5px' }}>
                    <FaIcon
                      icon='user-lock'
                      typographyProps={{ variant: 'xl', color: 'primary' }}
                    />
                  </IconButton>
                </a>
              </Tooltip>
            </Grid>
            <Grid
              item
              onDoubleClick={() => {
                setShowText(false);
              }}
            >
              <MDBox
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                <Tooltip
                  title={showTooltip ? row.original.fieldname : ''}
                  placement='top'
                >
                  <MDTypography
                    ref={typographyRef}
                    color='info'
                    variant='sm'
                    sx={{ textOverflow: 'ellipsis' }}
                  >
                    {row.original.fieldname}
                  </MDTypography>
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        ) : (
          <MDBox
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            <Tooltip
              title={showTooltip ? row.original.fieldname : ''}
              placement='top'
            >
              <MDTypography
                ref={typographyRef}
                color='info'
                variant='sm'
                onDoubleClick={() => {
                  setShowText(false);
                }}
                sx={{
                  textOverflow: 'ellipsis'
                }}
              >
                {row.original.fieldname}{' '}
              </MDTypography>
            </Tooltip>
          </MDBox>
        )}
      </>
    ) : (
      <>
        <ClickAwayListener onClickAway={handleEnterAndAwayClick}>
          <MDInput
            sx={{ backgroundImage: 'none' }}
            value={value}
            error={error}
            onChange={onInputChange}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === 'Escape')
                handleEnterAndAwayClick();
            }}
          />
        </ClickAwayListener>
        {error && errorMessage && (
          <FormHelperText error>{errorMessage}</FormHelperText>
        )}
      </>
    );
  };

  const handleAcceptAndImportClick = () => {
    sendAmplitudeData(CLICKED_IMPORT_NEW_FILE);
    if (combineDataPayload?.id) {
      const columns = selectedSensitiveColumns.map(
        (column) => column.fieldname
      );

      if (columns.length) {
        const payload: NotificationData = {
          created_at: dayjs(new Date())
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          is_read: false,
          is_silent: true,
          is_user_message: true,
          message_event: MSG_EVENT.DATA_ANONYMIZED,
          message_text: `${columns?.join(', ')} ${
            columns.length > 1 ? 'have' : 'has'
          } been automatically anonymised. Read more about this in <click here>`,
          updated_at: dayjs(new Date())
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          message_type: MSG_TYPE.INFO,
          message_data: JSON.stringify({
            project: { id: projectId || '', name: projectName }
          }),
          is_important: false,
          recipient_obj: 0,
          linkKeyword: 'What is Data Anonymisation, and Why?',
          replaceKeyword: '<click here>',
          isAnchorElement: true,
          feature_name: 'Sensitive Data'
        };

        dispatch(setUserNotifications(payload));
      }

      const selectedDataTypes = combineProjectFieldList.fields.map(
        (field: any) => field.treat_as_type
      );

      const changeInDataTypeFlag = defaultDataTypes.filter(
        (dataType: string, index: number) =>
          selectedDataTypes[index] === dataType
      );

      sendAmplitudeData(FILE_REVIEW, {
        'File Type': combineProjectFieldList?.file_type,
        'File ID': projectId,
        'Name of the file/project': projectName,
        'Change in Data Type':
          changeInDataTypeFlag.length === selectedDataTypes.length
            ? 'No'
            : 'Yes',
        'Change in Data Anonymisation': columns.length ? 'Yes' : 'No'
      });
      sendGAData(FILE_REVIEW, {
        'File Type': combineProjectFieldList?.file_type,
        'File ID': projectId,
        'Name of the file/project': projectName,
        'Change in Data Type':
          changeInDataTypeFlag.length === selectedDataTypes.length
            ? 'No'
            : 'Yes',
        'Change in Data Anonymisation': columns.length ? 'Yes' : 'No'
      });

      const notificationPayload: NotificationData = {
        created_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        is_read: false,
        is_silent: false,
        is_user_message: true,
        message_event: MSG_EVENT.FILE_COMBINE_STARTED,
        message_text:
          'Your files are being combined. Once complete,\n1. A clean check will be run on your data again\n2. Existing charts on your Dashboard will be updated\n3. You can download combined cleaned data from under Download\nYou will be notified once it is complete!',
        updated_at: dayjs(new Date())
          .utc()
          .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
        message_type: MSG_TYPE.INFO,
        message_data: JSON.stringify({
          project: {
            id: projectBoard?.currentProjectData?.id,
            name: projectBoard?.currentProjectData?.project_name
          }
        }),
        linkKeyword: 'here.',
        replaceKeyword: '<click here>',
        is_important: false,
        recipient_obj: 0,
        isAnchorElement: true
      };

      dispatch(setUserNotifications(notificationPayload));
      combineDataFiles(combineDataPayload);
    }
  };

  const ReviewTableColumns = useMemo(
    () => [
      {
        Header: () => (
          <Tooltip
            title='Turn the button On or Off for a column. Only columns selected as ON will be imported and saved.'
            placement='top'
          >
            <span>Import</span>
          </Tooltip>
        ),
        accessor: 'to_import',
        width: '5%',
        Cell: ({ cell }: any) => (
          <div>
            <Tooltip
              title={
                cell.row.values.treat_as_type === 'EMPTY'
                  ? 'Empty fields will not be imported.'
                  : cell.row.original.id ===
                    combineDataPayload?.selected_source_keyfield
                  ? 'This column will be imported as it is the key column selected/confirmed by you.'
                  : ''
              }
              placement='top'
            >
              <div>
                <Switch
                  checked={!!cell.row.original.to_import}
                  onChange={(e) => {
                    if (
                      cell.row.original.id ===
                      combineDataPayload?.selected_source_keyfield
                    )
                      return;

                    handleImportSwitchChange(
                      e,
                      cell.row.original.id,
                      cell.row.values.treat_as_type,
                      cell
                    );
                  }}
                  disabled={cell.row.values.treat_as_type === 'EMPTY'}
                  color='default'
                />
              </div>
            </Tooltip>
          </div>
        )
      },
      {
        Header: () => (
          <Tooltip
            title='Column Number as in your uploaded file.'
            placement='top'
          >
            <span>#</span>
          </Tooltip>
        ),
        accessor: 'index',
        width: '5%',
        Cell: ({ cell }: any) => (
          <>{convertToExcelColumnNumber(Number(cell.row.id) + 1)}</>
        )
      },
      {
        Header: () => (
          <Tooltip
            title='Column Name as in your uploaded file. You can change it if you like.'
            placement='top'
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 2,
                width: 'min-content'
              }}
            >
              <span style={{ width: 'max-content' }}>Column Name</span>
              <FaIcon
                icon='circle-question'
                style={{
                  verticalAlign: 'middle'
                }}
                typographyProps={{
                  lineHeight: 0.625,
                  color: 'info'
                }}
              />
            </div>
          </Tooltip>
        ),
        accessor: 'fieldname',
        width: '20%',
        Cell: EditableCell
      },
      {
        Header: () => (
          <Tooltip
            title='Type of column as determined by DataSquirrel. You can change it in case it is incorrectly determined - it helps us improve our recommendations for you'
            placement='right'
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 2,
                width: 'min-content'
              }}
            >
              <span>Type</span>
              <FaIcon
                icon='circle-question'
                style={{
                  verticalAlign: 'middle'
                }}
                typographyProps={{
                  color: 'info',
                  lineHeight: 0.625
                }}
              />
            </div>
          </Tooltip>
        ),
        accessor: 'treat_as_type',
        width: '10%',
        Cell: ({ cell }: any) => (
          <>
            <Select
              sx={{
                padding: '8px',
                minWidth: '125px'
              }}
              defaultValue={
                excludedTypes.includes(cell.row.values.treat_as_type)
                  ? 'DATETIME'
                  : cell.row.values.treat_as_type
              }
              disabled={cell.row.values.treat_as_type === ColumnTypes.EMPTY}
              onChange={(e) =>
                handleColumnTypeChange(
                  e,
                  cell.row.original.id,
                  cell.row.values.to_import
                )
              }
              IconComponent={ExpandMore}
            >
              {cell.row.values.treat_as_type === ColumnTypes.EMPTY
                ? combineProjectFieldList.type_choices.map((type: any) => (
                    <MenuItem key={type[0]} value={type[0]}>
                      <MDTypography variant='sm' color='info'>
                        {type[1] === 'Id' ? 'ID' : type[1]}
                      </MDTypography>
                    </MenuItem>
                  ))
                : combineProjectFieldList.type_choices
                    .filter((type: any) => type[0] !== ColumnTypes.EMPTY)
                    .map((type: any) => (
                      <MenuItem key={type[0]} value={type[0]}>
                        <MDTypography variant='sm' color='sm'>
                          {type[1] === 'Id' ? 'ID' : type[1]}
                        </MDTypography>
                      </MenuItem>
                    ))}
            </Select>
          </>
        )
      },
      {
        accessor: 'hoverItem',
        width: '60%',
        Cell: ({ cell }: any) => {
          const typographyRef = useRef<HTMLDivElement>();

          const showTooltip =
            typographyRef?.current &&
            typographyRef?.current?.getBoundingClientRect().height > 19
              ? true
              : false;

          return (
            <Tooltip
              title={
                showTooltip
                  ? getColumnFacts(
                      cell.row.original.treat_as_type,
                      JSON.parse(cell.row.original.data_facts),
                      cell.row.original.empty_rows,
                      cell.row.original.unique_values
                    )
                  : ''
              }
              placement='top'
            >
              <MDBox
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                <MDTypography
                  ref={typographyRef}
                  color='info'
                  variant='sm'
                  sx={{
                    textOverflow: 'ellipsis'
                  }}
                >
                  {getColumnFacts(
                    cell.row.original.treat_as_type,
                    JSON.parse(cell.row.original.data_facts),
                    cell.row.original.empty_rows,
                    cell.row.original.unique_values
                  )}
                </MDTypography>
              </MDBox>
            </Tooltip>
          );
        }
      }
    ],
    [combineProjectFieldList]
  );

  // hook to update combine data field list in state
  useEffect(() => {
    if (data) dispatch(setCombineProjectFields(data));
  }, [data]);

  // hook to clear combine data field list in state on unmount
  useEffect(
    () => () => {
      dispatch(setCombineProjectFields([]));
    },
    []
  );

  // hook to set sensitive field list in state
  useEffect(() => {
    setSelectedSensitiveColumns(
      (combineProjectFieldList?.fields as SensitiveCell[])?.filter(
        (row) => row.contains_sensitive && row.to_import
      )
    );
  }, [combineProjectFieldList?.fields]);

  // hook top toggle allert snack bar
  useEffect(() => {
    if (openAlertSnackBar) setTimeout(() => setOpenAlertSnackBar(false), 6000);
  }, [openAlertSnackBar]);

  // Hook to fetch project for combine or cobine cancel success
  useEffect(() => {
    if (isCombineSuccess) setInitiatePolling(true);
  }, [isCombineSuccess]);

  if (!data && isLoading) return <FullPageLoader />;

  return (
    <>
      <If
        condition={
          !!combineProjectFieldList && combineProjectFieldList?.length !== 0
        }
      >
        <Grid
          display='flex'
          container
          sx={{ mt: '1rem' }}
          flexDirection='column'
        >
          <Grid
            item
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            mb={2}
            width='100%'
          >
            <MDBox
              display='flex'
              alignContent='center'
              alignItems='center'
              width='90%'
              overflow='hidden'
            >
              <Tooltip
                title={`Confirm the columns in
                    ${combineDataPayload?.source_datafile?.filename} to be
                    processed`}
                placement='bottom'
              >
                <MDTypography
                  variant='h3'
                  color='info'
                  textGradient
                  sx={{
                    maxWidth: '96%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxHeight: '120px'
                  }}
                >
                  Confirm the columns in{' '}
                  {combineDataPayload?.source_datafile?.filename} to be
                  processed
                </MDTypography>
              </Tooltip>
            </MDBox>
            <MDBox width='40%' display={'flex'} justifyContent={'flex-end'}>
              <MDButton
                size='large'
                color='secondary'
                variant='outlined'
                onClick={() => {
                  handleCancelCombine();
                }}
                sx={{ marginRight: '15px' }}
              >
                Cancel
              </MDButton>
              <Tooltip
                title='Confirm all sections and then click here'
                placement='top'
              >
                <MDButton
                  variant='gradient'
                  color='primary'
                  size='large'
                  onClick={handleAcceptAndImportClick}
                >
                  ACCEPT & IMPORT
                </MDButton>
              </Tooltip>
            </MDBox>
          </Grid>
        </Grid>
      </If>
      {/* <If condition={!combineProjectFieldList || combineProjectFieldList.length === 0}>
        <Grid container height='80px'></Grid>
      </If> */}

      <Grid container sx={{ height: 'calc(100vh - 280px)' }}>
        <DataTable
          table={{
            columns: ReviewTableColumns,
            rows: combineProjectFieldList?.fields || []
          }}
          showTotalEntries={false}
          entriesPerPage={{
            defaultValue: combineProjectFieldList?.fields?.length || 10,
            entries: []
          }}
        />
      </Grid>
      {openAlertSnackBar && (
        <Snackbar
          open={openAlertSnackBar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Alert
            severity='info'
            sx={{ width: '100%' }}
            onClose={() => setOpenAlertSnackBar(false)}
          >
            Looks like this column contains sensitive data. It will be
            automatically anonymised by us, unless you choose to NOT anonymise
            it in “Choose Clean Options” step. Read more in{' '}
            <span
              role='presentation'
              onClick={() =>
                sendAmplitudeData(CLICKED_KNOW_MORE, {
                  'Feature Name': 'Sensitive Data'
                })
              }
            >
              <Link
                href='https://datasquirrel.notion.site/What-is-Sensitive-Data-Data-Anonymisation-17f688499d2045d99ec677c037f0c290'
                sx={{ borderBottom: '1px solid #000' }}
                target='_blank'
              >
                What is Data Anonymisation, and Why?
              </Link>
            </span>
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default CombineReviewTable;
